/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// disabled because: let the compiler infer the types
import { createSelector } from 'reselect';
import { elevioApi } from '../utils/apis/graphql/api';
import { extractConversationTextFromMessages } from '../utils/messages';
/**
 * Returns the selected articleId for Elevio suggestions.
 *
 * Returns `null` if there is no:
 *
 * - ConversationId
 * - ConversationState
 * - ArticleIds
 */
const articleId = (store) => {
    const conversationId = store.conversation.metaConversation.conversation.id;
    if (!conversationId)
        return null;
    const conversationState = store.elevio.suggestions.conversationStore[conversationId];
    if (!conversationState)
        return null;
    const articleIds = conversationState.articleIds;
    if (!articleIds)
        return null;
    return articleIds.length > 0 ? articleIds[articleIds.length - 1] : null;
};
/** Elevio auth state */
const authState = (store) => store.elevio.suggestions.authState;
/** All suggestion data for all conversations */
const conversationStore = (store) => store.elevio.suggestions.conversationStore;
/** All search data for all conversations */
const searchStore = (store) => store.elevio.search.searchStore;
const resolutionSelectors = {
    articleDropdownOpen: (store) => store.elevio.resolution.articleDropdownOpen,
    articleSearchTerm: (store) => store.elevio.resolution.articleSearchTerm,
    articleSolved: (store) => store.elevio.resolution.articleSolved,
    draft: (store) => store.elevio.resolution.draft,
    previousState: (store) => store.elevio.resolution.previousState,
    selectedArticleId: (store) => store.elevio.resolution.selectedArticleId,
    articleSearchInputValue: (store) => store.elevio.resolution.articleSearchInputValue,
    selectedArticleTitle: (store) => store.elevio.resolution.selectedArticleTitle,
    stage: (store) => store.elevio.resolution.stage,
};
/** Selects conversation Tag[] */
const tagsSelector = createSelector((store) => {
    var _a, _b, _c;
    const conversationId = store.conversation.metaConversation.conversation.id;
    if (!conversationId)
        return [];
    const conversationKey = `readConversation({"csid":${conversationId}})`;
    const conversation = (_a = store.api) === null || _a === void 0 ? void 0 : _a.queries[conversationKey];
    const conversationTags = ((_c = (_b = conversation === null || conversation === void 0 ? void 0 : conversation.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.history.main_conversation.tags) || [];
    const legacyTags = store.conversation.metaConversation.conversation.tags || [];
    const tags = !conversation || !conversationTags ? legacyTags : conversationTags;
    return tags;
}, (tags) => tags);
const messagesSelector = createSelector((store) => store.conversation.metaConversation.messages, (messages) => messages);
const conversationSelectors = {
    id: (store) => store.conversation.metaConversation.conversation.id || null,
    status: (store) => store.conversation.metaConversation.conversation.status,
    tags: tagsSelector,
    /** Selects tags as a string[] */
    tagNames: createSelector(tagsSelector, (tags) => tags.map((tag) => tag.name)),
    subject: (store) => { var _a, _b; return (_b = (_a = store.conversation.metaConversation.conversation.attributes) === null || _a === void 0 ? void 0 : _a.subject) !== null && _b !== void 0 ? _b : ''; },
    /** List of messages in a conversation */
    messages: messagesSelector,
    /** List of messages in a conversation as a single piece of text */
    text: createSelector(messagesSelector, (messages) => extractConversationTextFromMessages(messages)),
    /** Are tags loaded */
    tagsLoaded: (store) => store.conversation.asyncStatus.tags,
    /** The current agents id */
    agentId: (store) => {
        var _a;
        return ((_a = store.conversation.metaConversation.conversation.assignment) === null || _a === void 0 ? void 0 : _a.agent_id) ||
            null;
    },
};
/** List of ArticleSuggestion[] for the current conversation */
const suggestionsForCurrentConversation = createSelector(conversationSelectors.id, conversationStore, (conversationId, conversationState) => {
    var _a;
    if (!conversationId)
        return [];
    return ((_a = conversationState[conversationId]) === null || _a === void 0 ? void 0 : _a.suggestions) || [];
});
/** Conversation state for the current conversastion */
const conversationStoreForCurrentConversation = createSelector(conversationSelectors.id, conversationStore, (conversationId, conversationState) => {
    if (!conversationId)
        return;
    return conversationState[conversationId];
});
/** List of article suggestion id's for the current conversation */
const suggestedArticleIds = createSelector(suggestionsForCurrentConversation, (suggestions) => {
    return suggestions.map((suggestion) => suggestion.id);
});
/** Suggestions are fetching */
const suggestionsFetching = (store) => store.elevio.suggestions.fetching;
/** There was an error fetching suggestions */
const suggestionErrorMessage = (store) => store.elevio.suggestions.errorMessage || undefined;
const unableToFindTaggedSuggestions = createSelector(conversationSelectors.id, conversationStore, (conversationId, conversationState) => {
    if (!conversationId)
        return [];
    return conversationState[conversationId].unableToFindTaggedSuggestions;
});
const getDetectedLanguageId = (conversationId) => (store) => conversationId
    ? store.elevio.suggestions.languages.detectedLanguages[conversationId]
    : null;
/** The default language selected in Elevio at present */
function getDefaultLanguageSelector(store) {
    const defaultLanguage = Object.assign(Object.assign({}, store.elevio.suggestions.languages.defaultLanguage), { mode: 'defaultLanguage' });
    return defaultLanguage;
}
/** The languages available in Elevio at present */
function getAvailableLanguages(store) {
    const availableLanguages = store.elevio.suggestions.languages.availableLanguages.results || [];
    const availableLanguageIds = availableLanguages.map((language) => language.id);
    return { availableLanguages, availableLanguageIds };
}
/**
 * Maps a detected language, if available, to a matching Elevio language, if
 * available.
 */
function getAutoDetectedLanguageSelector(store) {
    const conversationId = conversationSelectors.id(store);
    const detectedLanguageId = getDetectedLanguageId(conversationId)(store);
    const { availableLanguages, availableLanguageIds } = getAvailableLanguages(store);
    const autoDetectedLanguage = !!detectedLanguageId && availableLanguageIds.includes(detectedLanguageId)
        ? Object.assign(Object.assign({}, availableLanguages.find((lang) => lang.id === detectedLanguageId)), { mode: 'autoDetectedLanguage' }) : undefined;
    return autoDetectedLanguage;
}
/**
 * Takes the stored language id and checks if it matches against any of the
 * currently available languages in Elevio. It's possible that a language can be
 * deactivated, so this helps cater for that situation.
 */
function getMatchedLanguageSelector(store) {
    const { availableLanguages } = getAvailableLanguages(store);
    const storedSelectedLanguageId = store.settings.personal.elevioSelectedLanguage; // it could be an empty string in the store
    const matchedAvailableLanguage = availableLanguages.find((lang) => {
        return lang.id === storedSelectedLanguageId;
    });
    const matchedLanguage = matchedAvailableLanguage && {
        mode: 'userSelectedLanguage',
        id: matchedAvailableLanguage.id,
        nativeName: matchedAvailableLanguage.nativeName,
    };
    return matchedLanguage;
}
function languagesSelector(store) {
    const defaultLanguage = getDefaultLanguageSelector(store);
    const autoDetectedLanguage = getAutoDetectedLanguageSelector(store);
    const matchedLanguage = getMatchedLanguageSelector(store);
    /**
     * Could be an empty string in the store, or it could be an invalid
     * 'deactivated' language id that is no longer active in Elevio.
     */
    const persistedElevioLanguageValue = store.settings.personal.elevioSelectedLanguage;
    const storedSelectedLanguage = matchedLanguage || autoDetectedLanguage || defaultLanguage;
    const languageStore = Object.assign(Object.assign({}, store.elevio.suggestions.languages), { selectedLanguage: storedSelectedLanguage, selectedLanguageId: storedSelectedLanguage.id, selectedLanguageMode: persistedElevioLanguageValue });
    return languageStore;
}
/** Required so that changes to languageId are picked up by useAppSelector */
const selectedLanguageId = createSelector(languagesSelector, (lang) => lang.selectedLanguage.id || '');
const suggestionSelectors = {
    authState,
    /** Another name for Elevio workspace ID */
    companyUid: (store) => store.elevio.suggestions.companyUid,
    token: (store) => store.elevio.suggestions.token,
    subscriptionStatus: (store) => store.elevio.suggestions.subscriptionStatus,
    conversationStore: conversationStore,
    isFetching: suggestionsFetching,
    suggestedArticleIds,
    suggestionsForCurrentConversation,
    conversationStoreForCurrentConversation,
    errorMessage: suggestionErrorMessage,
    unableToFindTaggedSuggestions,
    languages: languagesSelector,
    selectedLanguageId,
    defaultLanguage: (store) => store.elevio.suggestions.languages.defaultLanguage,
    getDetectedLanguage: getDetectedLanguageId,
};
const searchSelectors = {
    searchStore,
    /** Input value in the UI */
    searchInputValue: (store) => store.elevio.search.searchInputValue,
    /**
     * Selects an Elevio searchterm for a given conversationId. If the
     * conversation does not exist, returns ''.
     */
    searchTerm: createSelector(conversationSelectors.id, searchStore, (id, store) => {
        return id ? (store[id] ? store[id].searchTerm : '') : '';
    }),
};
const userSelectors = {
    /** Agents email */
    email: (store) => store.elevio.suggestions.userEmail,
    /** KCS settings for the user - returns KcsSettingsQuery */
    kcsSettings: (store) => elevioApi.grappa.endpoints.kcsSettings.select({})(store),
};
const aiResponseSelectors = {
    data: (store) => store.elevio.aiResponse.data,
    loading: (store) => store.elevio.aiResponse.loading,
    tabStatus: (store) => store.elevio.aiResponse.tabStatus,
};
export const elevioSelectors = {
    articleFeedback: (store) => store.elevio.feedback,
    articleId,
    search: searchSelectors,
    suggestion: suggestionSelectors,
    resolution: resolutionSelectors,
    conversation: conversationSelectors,
    user: userSelectors,
    aiResponse: aiResponseSelectors,
};
